import { FC, useState } from 'react';
import { BsThreeDotsVertical } from 'react-icons/bs';
import {
  Box,
  BoxProps,
  CheckboxCheckedChangeDetails,
  Flex,
  Heading,
  Menu,
  Portal,
  Text,
} from '@chakra-ui/react';

import { useUpdateApplicationTransitionRequirement } from '~/core/shared/api/middle-office/application';
import { DateTimeDisplay } from '~/core/shared/components/DateTimeDisplay';
import { useEventHandler } from '~/core/shared/hooks/useEventHandler';
import { Checkbox } from '~/core/shared/lib/chakraUi/components/Checkbox';
import {
  DocumentTransitionRequirementResource,
  DocumentTransitionRequirementStatus,
} from '~/core/shared/types';
import { Button } from './Button.component';

type DocumentRequirementsChecklistProps = {
  documentUuid: string;
  documentDefinition: string;
  workflow: string;
  transition: string;
  requirements: DocumentTransitionRequirementResource[];
  onRequirementUpdate?: (
    requirement: string,
    status: DocumentTransitionRequirementStatus,
  ) => void;
} & BoxProps;

export const DocumentRequirementsChecklist: FC<
  DocumentRequirementsChecklistProps
> = ({
  documentUuid,
  documentDefinition,
  workflow,
  transition,
  requirements: initialRequirements,
  onRequirementUpdate,
  ...props
}) => {
  const { updateTransitionRequirement } =
    useUpdateApplicationTransitionRequirement(documentDefinition, workflow);
  const [requirements, setRequirements] =
    useState<DocumentTransitionRequirementResource[]>(initialRequirements);
  const [requirementLoading, setRequirementLoading] = useState<
    Record<string, boolean>
  >({});

  const completedRequirements = requirements.filter(
    (requirement) => requirement.status === 'pass',
  ).length;

  const updateRequirementStatus = useEventHandler(
    async (
      requirement: string,
      status: DocumentTransitionRequirementStatus,
    ): Promise<void> => {
      setRequirementLoading({ ...requirementLoading, [requirement]: true });

      const response = await updateTransitionRequirement({
        uuid: documentUuid,
        data: { requirement, status, transition, workflow },
      });

      setRequirementLoading({ ...requirementLoading, [requirement]: false });

      const responseRequirement = response?.data;
      if (responseRequirement) {
        setRequirements(
          requirements.map((r) =>
            r.key === responseRequirement.key ? responseRequirement : r,
          ),
        );
        onRequirementUpdate?.(requirement, status);
      }
    },
  );

  return (
    <Box {...props}>
      <Heading as="h4" size="sm">
        Checklist
      </Heading>
      <Text>
        {completedRequirements} of {requirements.length} completed
      </Text>
      <Box
        border="1px solid"
        borderColor="gray.100"
        maxH="calc(100vh - 330px)"
        overflowX="hidden"
        overflowY="auto"
        rounded="md"
      >
        {requirements.map((requirement, index) => {
          const date = (
            <DateTimeDisplay fallback="" value={requirement.statusAt} />
          );
          const isLast = index === requirements.length - 1;
          const menuContentProps = {
            p: 0,
            right: 'calc(100% - 32px)',
            top: isLast ? '-62px' : '34px',
          };
          return (
            // eslint-disable-next-line react/no-array-index-key
            <Box key={index}>
              <Flex
                alignItems="center"
                bgColor={
                  requirement.status === 'pass' ? 'blue.50' : 'transparent'
                }
                borderBottom={isLast ? 'none' : '1px solid'}
                borderColor="gray.100"
                direction="row"
                justifyContent="space-between"
                p="4"
                position="relative"
              >
                <Checkbox
                  borderColor="gray.300"
                  checked={requirement.status === 'pass'}
                  disabled={requirementLoading[requirement.key] || false}
                  flexShrink={0}
                  mr={4}
                  onCheckedChange={(e: CheckboxCheckedChangeDetails) =>
                    updateRequirementStatus(
                      requirement.key,
                      e.checked ? 'pass' : 'unchecked',
                    )
                  }
                  size="lg"
                />

                <Box
                  flex="1"
                  opacity={requirement.status !== 'unchecked' ? 0.5 : 1}
                >
                  <Text
                    textDecoration={
                      requirement.status === 'not_applicable'
                        ? 'line-through'
                        : 'none'
                    }
                  >
                    {requirement.title}
                  </Text>
                  {requirement.status === 'unchecked' &&
                    requirement.statusBy && (
                      <Text fontSize="sm" opacity="0.5">
                        Unchecked by {requirement.statusBy} ({date})
                      </Text>
                    )}
                  {requirement.status === 'pass' && (
                    <Text fontSize="sm">
                      By {requirement.statusBy} ({date})
                    </Text>
                  )}
                  {requirement.status === 'not_applicable' && (
                    <Text fontSize="sm">
                      Marked as N/A by {requirement.statusBy} ({date})
                    </Text>
                  )}
                </Box>

                <Box ml={4} position="relative">
                  <Menu.Root>
                    <Menu.Trigger asChild>
                      <Button
                        border="none"
                        colorPalette="gray"
                        h="32px"
                        minW="inherit"
                        p={0}
                        pl={2}
                        textAlign="center"
                        w="32px"
                      >
                        <BsThreeDotsVertical />
                      </Button>
                    </Menu.Trigger>
                    <Portal>
                      <Menu.Positioner>
                        <Menu.Content position="relative" {...menuContentProps}>
                          <Menu.Item
                            onClick={() =>
                              updateRequirementStatus(
                                requirement.key,
                                'not_applicable',
                              )
                            }
                            value="mark-as-na"
                          >
                            Mark as not applicable
                          </Menu.Item>
                        </Menu.Content>
                      </Menu.Positioner>
                    </Portal>
                  </Menu.Root>
                </Box>
              </Flex>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};
